import _ from "lodash";
import { getGridStickyDefaults } from "../../../../app/core/util/stickyDefaults";
import * as z from "../../../../framework";
import { ll } from "../../../core/ll";
import { user } from "../../../core/user";
import { codes } from "../../../data/codes";
import * as schema from "../../../data/schema";
import { areGridsEditable, GridColumn } from "../grid";

export function build(args) {
    var excludedProperties = ["IsDeleted", "CreatedGroup"];

    var laborCodesOption = _.find(z.user.companyData.Options, { OptionNumber: "LaborCodes" });
    if (!laborCodesOption || laborCodesOption.Value.toLowerCase() == "false") {
        excludedProperties.push("LaborCodeId", "DefaultLaborCodeId");
    }

    var columns = buildForEntity({
        rootEntityName: args.entityName,
        entityName: args.entityName,
        accessPath: "",
        expandPath: "",
        expand: args.expand,
        excludedProperties: excludedProperties
    });

    _.each(args.expand, function (expand) {
        var navigationPropertyInfo = schema.getNavigationPropertyInfo({ entityName: args.entityName, path: expand });

        var expandColumns = buildForEntity({
            rootEntityName: args.entityName,
            entityName: navigationPropertyInfo.entityName,
            accessPath: navigationPropertyInfo.accessPath,
            expandPath: navigationPropertyInfo.expandPath,
            expand: args.expand,
            excludedProperties: excludedProperties
        });

        columns = _.concat(columns, expandColumns);
    });

    if (args.forUserGridTabScreen === true) {
        columns = filterAvailable({ columns: columns, availableColumns: args.availableColumns, expand: args.expand });
        columns = addUtilityColumns({ columns: columns, utilityColumns: args.utilityColumns });
    } else {
        columns = hideAll({ columns: columns });
        columns = filterAvailable({ columns: columns, availableColumns: args.availableColumns, expand: args.expand });
        columns = addUtilityColumns({ columns: columns, utilityColumns: args.utilityColumns });
        columns = orderAndShow({ columns: columns, columnOrder: args.columnOrder });
        columns = overrideDefaults({ columns: columns, columnOverrides: args.columnOverrides });
        columns = loadStickyDefaults({ columns: columns, entityName: args.entityName, stickyDefaultName: args.stickyDefaultName });
        columns = filterColumnsIfNotEditable({ columns: columns });
        columns = renameColumns({ columns: columns, columnRenames: args.columnRenames });
        columns = twoColumnMinimum(columns);
        columns = displayFirstItemInCollections({ columns, collections: args.displayFirstItemInCollections });
    }

    return columns;
}

function buildForEntity(args) {
    if (args.expandPath === "") {
        try {
            args.localizedEntityName = schema.localizeEntity({ entityName: args.entityName });
        } catch (e) {
            args.localizedEntityName = ll(args.entityName);
        }
    } else {
        try {
            var navigationPropertyInfo = schema.getNavigationPropertyInfo({ path: `${args.rootEntityName}/${args.expandPath}` });
            args.localizedEntityName = schema.localizeNavigationProperty({ entityName: navigationPropertyInfo.parentEntityName, navigationPropertyName: navigationPropertyInfo.navigationPropertyName });
        } catch (e) {
            args.localizedEntityName = schema.splitFirst(args.expandPath)[0];
        }
    }

    var propertyColumns = buildForEntityProperties(args),
        udfColumns = buildForEntityUDFs(args);

    return _.concat(propertyColumns, udfColumns);
}

function buildForEntityProperties(args) {
    var entitySchema = schema.getSchema(args.entityName);
    var properties = _.omitBy(entitySchema.Properties, (value, _key) => value.IsDeprecated);
    var propertyNames = _(properties)
        .keys()
        .without(...args.excludedProperties)
        .value();
    var columns = [];

    _.forEach(propertyNames, function (propertyName) {
        var property = properties[propertyName],
            field = args.accessPath
                ? `${args.accessPath}.${propertyName}`
                : propertyName,
            title = schema.localizeProperty({ entityName: args.rootEntityName, field });

        if (propertyName == "CreatedUserId" || propertyName == "LastUpdatedUserId") {
            columns.push({
                field: field,
                template: getTemplateForUserId({ field: field }),
                sortable: false,
                filterable: false,
                title: title,
                selectPath: schema.combinePaths(args.expandPath, propertyName),
                localizedEntityName: args.localizedEntityName
            });
        } else if (!property.ForeignKeyType) {
            columns.push({
                field: field,
                template: getTemplate({ type: property.Type, field: field }),
                title: title,
                selectPath: schema.combinePaths(args.expandPath, propertyName),
                localizedEntityName: args.localizedEntityName
            });
        } else if (codes[property.ForeignKeyType] && (propertyName != "RecordId" && property.ForeignKeyType != "Accessory")) {
            var navigationPropertyName = schema.getMappedNavigationPropertyName({ entityName: args.entityName, propertyName: propertyName }),
                isExpanded = _.find(args.expand, function (expand) { return expand == schema.combinePaths(args.expandPath, navigationPropertyName); }),
                fullNavigation = args.accessPath
                    ? `${args.accessPath}.${navigationPropertyName}`
                    : navigationPropertyName,
                codeFields = schema.getCodeDisplayFields({ entityName: property.ForeignKeyType });

            if (!isExpanded && !entitySchema.NavigationProperties[navigationPropertyName].IsDeprecated) {
                columns.push({
                    field: `${fullNavigation}.${codeFields.primaryField}`,
                    template: getTemplateForCode({ field: field, entityName: property.ForeignKeyType, displayField: codeFields.primaryField }),
                    title: ll("CodeNumberFormat").format(title),
                    selectPath: schema.combinePaths(args.expandPath, navigationPropertyName, codeFields.primaryField),
                    isCode: true,
                    codeSelectPath: schema.combinePaths(args.expandPath, propertyName),
                    localizedEntityName: args.localizedEntityName
                });

                columns.push({
                    field: `${fullNavigation}.${codeFields.secondaryField}`,
                    template: getTemplateForCode({ field: field, entityName: property.ForeignKeyType, displayField: codeFields.secondaryField }),
                    title: ll("CodeValueFormat").format(title),
                    selectPath: schema.combinePaths(args.expandPath, navigationPropertyName, codeFields.secondaryField),
                    isCode: true,
                    codeSelectPath: schema.combinePaths(args.expandPath, propertyName),
                    localizedEntityName: args.localizedEntityName
                });
            }
        } else if (property.ForeignKeyType) {
            columns.push({
                field: field,
                template: getTemplate({ type: property.Type, field: field }),
                title: field.substring(field.indexOf(".") + 1, field.length),
                selectPath: schema.combinePaths(args.expandPath, propertyName),
                localizedEntityName: args.localizedEntityName
            });
        }
    });

    return columns;
}

function buildForEntityUDFs(args) {
    var udfProperties = _.filter(user.companyData.UserDefinedFields, function (udf) { return udf.TableName == args.entityName; });

    var columns = [];
    _.forEach(udfProperties, function (property) {
        var field = args.accessPath
            ? `${args.accessPath}.${property.BindName}`
            : property.BindName;

        if (property.Type == "SIGNATURE") {
            columns.push({
                field: field,
                template: getTemplateForSignature({ type: property.Type, field: field }),
                title: property.DisplayValue,
                selectPath: schema.combinePaths(args.expandPath, property.BindName),
                sortable: false,
                isUDF: true,
                localizedEntityName: args.localizedEntityName
            });
        } else if (property.Type == "URL") {
            columns.push({
                field: field,
                template: getTemplateForUrl({ type: property.Type, field: field }),
                title: property.DisplayValue,
                selectPath: schema.combinePaths(args.expandPath, property.BindName),
                sortable: false,
                isUDF: true,
                localizedEntityName: args.localizedEntityName
            });
        } else if (property.Type == "ENUM") {
            var navigationPropertyName = property.BindName.replace("UDF_", "UDFLV_"),
                fullNavigation = args.accessPath
                    ? `${args.accessPath}.${navigationPropertyName}`
                    : navigationPropertyName,
                primaryField = "CodeNumber",
                secondaryField = "Value";

            columns.push({
                field: `${fullNavigation}.${primaryField}`,
                template: getTemplateForUDFLV({ field: field, userDefinedListId: property.UserDefinedListId, displayField: primaryField }),
                title: ll("CodeNumberFormat").format(property.DisplayValue),
                selectPath: schema.combinePaths(args.expandPath, navigationPropertyName, primaryField),
                sortable: false,
                isUDFLV: true,
                codeSelectPath: schema.combinePaths(args.expandPath, property.BindName),
                localizedEntityName: args.localizedEntityName
            });

            columns.push({
                field: `${fullNavigation}.${secondaryField}`,
                template: getTemplateForUDFLV({ field: field, userDefinedListId: property.UserDefinedListId, displayField: secondaryField }),
                title: ll("CodeValueFormat").format(property.DisplayValue),
                selectPath: schema.combinePaths(args.expandPath, navigationPropertyName, secondaryField),
                sortable: false,
                isUDFLV: true,
                codeSelectPath: schema.combinePaths(args.expandPath, property.BindName),
                localizedEntityName: args.localizedEntityName
            });
        } else {
            columns.push({
                field: field,
                template: getTemplateForUDF({ type: property.Type, field: field }),
                title: property.DisplayValue,
                selectPath: schema.combinePaths(args.expandPath, property.BindName),
                sortable: false,
                isUDF: true,
                localizedEntityName: args.localizedEntityName
            });
        }
    });

    return columns;
}

function getTemplate(args) {
    var contents = getTemplateContents({ type: args.type });
    contents = contents.format("_.get(data, '{0}')".format(args.field));
    return "# var temp = {0}; #<span>#= temp === 0 ? '0' : (temp || '---') #</span>".format(contents);
}

function getTemplateContents(args) {
    switch (args.type) {
        case "Boolean":
            return "{0} && String({0}).toLowerCase() !== 'false' ? z.ll('BooleanTrue') : z.ll('BooleanFalse')";
        case "Currency":
            return "z.toString({0}, 'c')";
        case "Date":
            return "z.util.kendoFormatDate({0})";
        case "DateTime":
            return "z.util.kendoFormatDateTime({0})";
        case "OffsetTime":
            return "z.util.kendoFormatTime({0})";
        case "TimeOfDay":
            return "z.util.kendoFormatAbsoluteTime({0})";
        case "Binary":
        case "Byte":
        case "Decimal":
        case "Int16":
        case "Int32":
        case "Single":
        case "String":
        default:
            return "{0}";
    }
}

function getTemplateForUserId({ field }: { field: string; }) {
    return `<span>#= z.userCache.getEmail(_.get(data, "${field}")) ||  _.get(data, "${field}")#</span>`;
}

function getTemplateForCode(args) {
    return ("# var id = _.get(data, '{0}'); #" +
        "# var result = _.find(z.codes.{1}, function (c) { return c.Id == id; }); # " +
        "<span>#= result ? result.{2} : '---' #</span>").format(args.field, args.entityName, args.displayField);
}

function getTemplateForSignature(args) {
    return ("# var id = z.util.getChildDataFromObject(data, '{0}'); #" +
        "<a class='link'> " +
        "    <span class='grid-signature-link'>" +
        "        #= id ? z.ll('ViewSignature') : '---' #" +
        "    </span> " +
        "</a>").format(args.field);
}

function getTemplateForUrl(args) {
    return ("# var value = z.util.getChildDataFromObject(data, '" + args.field + "'); #" +
        "<a class='link'> " +
        "    <span class='grid-url-link'>" +
        "#       if (value) { #" +
        "#           var label = value.substring(0, value.indexOf('|')); #" +
        "#           var url = value.substring(value.indexOf('|') + 1); #" +
        "            #= label #" +
        "#       } else { #" +
        "            #= '---' #" +
        "#       } #" +
        "    </span> " +
        "</a>").format(args.field);
}

function getTemplateForUDF(args) {
    var typeMap = {
        "STRING": "String",
        "INT": "Int32",
        "FLOAT": "Decimal",
        "DATE": "Date",
        "TIME": "OffsetTime",
        "DATETIME": "DateTime",
        "CHECKBOX": "Boolean"
    };

    return getTemplate({ field: args.field, type: typeMap[args.type] });
}

function getTemplateForUDFLV(args) {
    if (args.userDefinedListId && args.field && args.displayField) {
        return "<span>#= z.cf.getCustomFieldListValue({0}, _.get(data, '{1}'), '{2}') #</span>".format(args.userDefinedListId, args.field, args.displayField);
    } else {
        return "---";
    }
}

function hideAll(args) {
    _.forEach(args.columns, function (column) {
        column.hidden = true;
    });
    return args.columns;
}

function filterAvailable(args) {
    return _.uniqBy(_.filter(args.columns as any[], function (column) {
        let foundInAvailableColumns = _.includes(args.availableColumns, column.selectPath);
        let shouldReturnColumn;

        let showIdsInGrids = _.find(user.companyData.UserPreferences, function (userPreference: any) {
            return userPreference.UserPreferenceNumber == "ShowIdsInGrids";
        });

        if (showIdsInGrids && showIdsInGrids.Value == "true") {
            let foundIdInMainEntityType = column.selectPath.substr(column.selectPath.length - 2) == "Id" && column.selectPath.indexOf("/") == -1;
            let foundIdInExpandedEntityType = _.includes(args.expand, column.selectPath.substring(0, column.selectPath.indexOf("/")));
            shouldReturnColumn = foundInAvailableColumns
                || foundIdInMainEntityType
                || foundIdInExpandedEntityType;
        } else {
            shouldReturnColumn = foundInAvailableColumns;
        }

        return shouldReturnColumn;
    }), "selectPath");
}

function orderAndShow(args) {
    var columns = [];

    // columnOrder columns first, in the order they appear in columnOrder
    _.forEach(args.columnOrder, function (columnPath) {
        var foundColumn = _.find(args.columns as any[], function (column) { return column.selectPath == columnPath; });
        if (foundColumn) {
            foundColumn.hidden = false;
            columns.push(foundColumn);
        }
    });

    // non-columnOrder columns after, in the order they appear in columns
    _.forEach(args.columns, function (column) {
        var defaultColumn = _.find(args.columnOrder, function (columnPath) { return column.selectPath == columnPath; });
        if (!defaultColumn) {
            columns.push(column);
        }
    });

    return columns;
}

function overrideDefaults(args) {
    var columns = _.map(args.columns as any[], function (column) {
        var override = _.find(args.columnOverrides as any[], function (o) { return column.selectPath == o.selectPath; });
        override = _.cloneDeep(override || {});
        if (override.field) {
            override.field = override.field.replaceAll("/", ".");
        }

        if (override.title) {
            override.title = override.localize !== false ? ll(override.title) : override.title;
        }

        return _.defaultsDeep(override, column);
    });

    return columns;
}

function loadStickyDefaults(args) {
    var field = args.stickyDefaultName;
    if (args.widgetId && !field) {
        field = args.widgetId;
    }

    var stickyDefaults = getGridStickyDefaults({ EntityName: args.entityName, Field: field }).Value || {};
    var defaultColumns = stickyDefaults.Columns || [];

    var columns = _.map(args.columns as any[], function (column) {
        var defaultColumn = _.find(defaultColumns as any[], function (c) { return c.field == column.field; });
        if (!defaultColumn) {
            return column;
        }

        if (defaultColumn.hidden !== undefined) {
            column.hidden = defaultColumn.hidden;
        }

        if (defaultColumn.width !== undefined) {
            if (column.id == "EditDelete") {
                column.width = column.command.length * 33;
            } else {
                column.width = defaultColumn.width;
            }
        }

        return column;
    });

    var defaultColumnOrder = _(defaultColumns).map(function (c) { return c.field; }).filter(function (c) { return c; }).value();
    var orderedColumns = [];

    // columnOrder columns first, in the order they appear in columnOrder
    _.forEach(defaultColumnOrder, function (columnField) {
        var foundColumn = _.find(args.columns as any[], function (column) { return column.field == columnField; });
        if (foundColumn) {
            orderedColumns.push(foundColumn);
        }
    });

    // non-columnOrder columns after, in the order they appear in columns
    _.forEach(args.columns, function (column) {
        var foundColumn = _.find(defaultColumnOrder, function (columnField) { return column.field == columnField; });
        if (!foundColumn) {
            orderedColumns.push(column);
        }
    });

    return orderedColumns;
}

function filterColumnsIfNotEditable(args) {
    var columns = args.columns;

    if (areGridsEditable()) {
        return columns;
    }

    return _.filter(args.columns, (column: any) => !column.hidden || column.isUtility);
}

function addUtilityColumns(args) {
    var utilityColumns = _.map(args.utilityColumns as any[], function (column) {
        column = _.cloneDeep(column);
        column.isUtility = true;
        column.sortable = column.sortable == undefined
            ? false
            : column.sortable;
        column.filterable = column.filterable == undefined
            ? false
            : column.filterable;
        column.selectPath = column.field;
        column.field = _.replace(column.field, "/", ".");

        if (column.title && column.localize !== false) {
            column.title = ll(column.title);
        }

        // This was added to set the localizedEntityName for utility columns that should be displayed on the Add Grid Tabs screen
        if (column.entityName) {
            if (column.expandPath === "") {
                try {
                    column.localizedEntityName = schema.localizeEntity({ entityName: column.entityName });
                } catch (e) {
                    column.localizedEntityName = ll(column.entityName);
                }
            } else {
                try {
                    var navigationPropertyInfo = schema.getNavigationPropertyInfo({ path: `${column.entityName}/${column.expandPath}` });
                    column.localizedEntityName = schema.localizeNavigationProperty({ entityName: navigationPropertyInfo.parentEntityName, navigationPropertyName: navigationPropertyInfo.navigationPropertyName });
                } catch (e) {
                    column.localizedEntityName = schema.splitFirst(column.expandPath)[0];
                }
            }
        }

        return column;
    });

    return _.concat(args.columns, utilityColumns);
}

function renameColumns(args) {

    _.forEach(args.columns, function (column) {
        if (args.columnRenames[column.field]) {
            column.title = ll(args.columnRenames[column.field]);
        }
    });

    return args.columns;
}

function twoColumnMinimum(columns) {
    let visibleColumnCount = _.filter(columns, function (column) {
        return !column.hidden;
    }).length;

    if (visibleColumnCount >= 2) {
        return columns;
    }

    let columnIndex = 0;
    while (visibleColumnCount < 2 && columnIndex < columns.length) {
        let field = columns[columnIndex].field;
        if (!field.endsWith("Id")) {
            columns[columnIndex].hidden = false;
            ++visibleColumnCount;
        }

        ++columnIndex;
    }

    return columns;
}

export function displayFirstItemInCollections({ columns, collections }: { columns: GridColumn[], collections: string[] }) {
    const modifiedColumns = columns.map(c => {
        if (_.isString(c.template) && _.isArray(collections)) {
            return ({
                ...c,
                template: collections.reduce(
                    (accumulator, currentValue) => accumulator.replaceAll(new RegExp(`\\b${currentValue}\\b`), `${currentValue}[0]`),
                    c.template)
            });
        } else {
            return c;
        }
    });
    return modifiedColumns;
}
